/**
 * 教师实时监控页面
 *
 * 三栏布局：
 *   左栏  - 学员列表（状态灯 / 故障 / 正确率，可滚动）
 *   中栏  - 选中学员的实时参数面板（只读，发散参数标红）
 *   右栏  - 全班实时操作流（最新在底部，自动滚动）
 *
 * 数据来源：useRealtimeStore（students / actionLog / status）
 * 进入页面自动 connect(token) + subscribeClass(classId)；卸载时仅 clearStudents，保持连接。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Radio,
  Users,
  Activity,
  RadioTower,
  Pause,
  MessageSquare,
  Square,
  Check,
  X,
  RefreshCw,
  PlayCircle,
  StopCircle,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { usePageStore } from '@/stores/pageStore';
import { useRealtimeStore } from '@/stores/realtimeStore';
import { getRealtimeClient } from '@/services/realtime';
import { getDemoSimulator } from '@/services/demoSimulator';
import { tokenStorage } from '@/services/api';
import type { StudentLiveStatus } from '@/services/realtime-types';

type ConnStatus = 'disconnected' | 'connecting' | 'connected';

/** 操作流条目（与 store 内 ActionLogEntry 结构一致） */
interface ActionLogEntry {
  userId: string;
  display_name: string;
  action: string;
  isCorrect: boolean;
  timestamp: number;
}

/** 相对时间："3s前" / "2m前" / "1h前" */
function timeAgo(ts: number | undefined, now: number): string {
  if (!ts) return '—';
  const diff = Math.max(0, now - ts);
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s前`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m前`;
  return `${Math.floor(diff / 3_600_000)}h前`;
}

/** HH:MM:SS */
function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('zh-CN', { hour12: false });
}

export function TeacherMonitorPage() {
  const user = useAuthStore((s) => s.user);
  const status = useRealtimeStore((s) => s.status);
  const students = useRealtimeStore((s) => s.students);
  const actionLog = useRealtimeStore((s) => s.actionLog);

  const classId = user?.class_id ?? null;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 每秒刷新一次，让"Xs前"保持新鲜
  const [now, setNow] = useState(() => Date.now());
  // 演示模式：开启后用 Mock 数据模拟学员演练，无需后端 WS
  const [demoMode, setDemoMode] = useState(false);

  // ---- 生命周期 ----

  // 进入页面：建立 WebSocket 连接（单例，重复调用安全）
  useEffect(() => {
    const token = tokenStorage.get();
    if (token) useRealtimeStore.getState().connect(token);
  }, []);

  // 连接状态变为 connected 时订阅班级（覆盖初次连接 + 断线重连后的重新订阅）
  const prevStatusRef = useRef<ConnStatus>('disconnected');
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (status === 'connected' && prev !== 'connected' && classId) {
      useRealtimeStore.getState().subscribeClass(classId);
    }
  }, [status, classId]);

  // 选中学员被清空时回退选择
  useEffect(() => {
    if (selectedId && !students[selectedId]) setSelectedId(null);
  }, [selectedId, students]);

  // 每秒刷新相对时间
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // 卸载时清空学员状态 + 停止演示模式（不 disconnect，保持连接）
  useEffect(() => {
    return () => {
      getDemoSimulator().stop();
      useRealtimeStore.getState().clearStudents();
    };
  }, []);

  // ---- 演示模式 ----

  /** 切换演示模式：开启时启动模拟器，关闭时停止并清空 */
  const toggleDemoMode = () => {
    const sim = getDemoSimulator();
    if (demoMode) {
      sim.stop();
      setDemoMode(false);
    } else {
      // 演示模式下不需要 WS 连接，直接用模拟数据
      useRealtimeStore.getState().clearStudents();
      sim.start();
      setDemoMode(true);
    }
  };

  // ---- 派生数据 ----

  const studentList = useMemo(() => Object.values(students), [students]);
  const selectedStudent = selectedId ? students[selectedId] ?? null : null;

  // 每个学员的 正确/总数（从全局操作流统计，作为实时"分数"代理）
  const statsByUser = useMemo(() => {
    const map: Record<string, { correct: number; total: number }> = {};
    for (const e of actionLog) {
      const s = map[e.userId] ?? { correct: 0, total: 0 };
      s.total++;
      if (e.isCorrect) s.correct++;
      map[e.userId] = s;
    }
    return map;
  }, [actionLog]);

  // ---- 动作 ----

  const handleRefresh = () => {
    if (classId) useRealtimeStore.getState().subscribeClass(classId);
  };

  /** 发送教师广播指令（暂停 / 提示 / 强制结束） */
  const handleBroadcast = (command: 'pause' | 'hint' | 'force-end', label: string) => {
    if (!classId) return;
    let message: string | undefined = undefined;
    if (command === 'hint') {
      const input = window.prompt('请输入要发送给学员的提示内容：', '注意观察参数趋势，及时调整。');
      if (input === null) return; // 用户取消
      message = input.trim() || undefined;
    } else {
      if (!window.confirm(`确认向全班发送「${label}」指令？`)) return;
    }
    const client = getRealtimeClient();
    if (!client) return;
    client.send({ type: 'teacher:broadcast', classId, command, message });
  };

  // =============================================================
  // 渲染
  // =============================================================
  return (
    <div className="flex flex-col h-[calc(100vh-60px)] bg-bg-primary text-text-primary">
      {/* ===== 顶部栏 ===== */}
      <header className="flex items-center justify-between px-4 py-2.5 bg-bg-secondary border-b border-border">
        <div className="flex items-center gap-3">
          <button
            onClick={() => usePageStore.getState().setPage('dashboard')}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            返回
          </button>
          <div className="h-5 w-px bg-border" />
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">教师实时监控</span>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {/* 班级选择器 */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-bg-tertiary border border-border rounded-lg">
            <Users className="w-3.5 h-3.5 text-text-muted" />
            <span className="text-xs text-text-muted">班级</span>
            <span className="text-xs font-mono text-text-primary">{classId ?? '未绑定'}</span>
          </div>

          {/* 连接状态指示器（演示模式时显示"演示中"） */}
          {demoMode ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-primary/15 border border-primary/40 rounded-lg">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs text-primary">演示中</span>
            </div>
          ) : (
            <ConnectionStatus status={status} />
          )}

          {/* 演示模式开关 */}
          <button
            onClick={toggleDemoMode}
            title={demoMode ? '停止演示模式' : '开启演示模式：模拟学员演练，无需后端连接'}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs border rounded-lg transition-colors ${
              demoMode
                ? 'bg-danger/15 text-danger border-danger/40 hover:bg-danger/25'
                : 'bg-primary/15 text-primary border-primary/40 hover:bg-primary/25'
            }`}
          >
            {demoMode ? <StopCircle className="w-3.5 h-3.5" /> : <PlayCircle className="w-3.5 h-3.5" />}
            {demoMode ? '停止演示' : '演示模式'}
          </button>

          {/* 刷新 */}
          <button
            onClick={handleRefresh}
            title="重新订阅班级实时数据"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-text-secondary hover:text-text-primary bg-bg-tertiary border border-border rounded-lg transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            刷新
          </button>
        </div>
      </header>

      {/* ===== 三栏主体 ===== */}
      <main className="flex-1 grid grid-cols-[200px_1fr_280px] min-h-0">
        {/* 左栏：学员列表 */}
        <section className="border-r border-border bg-bg-secondary flex flex-col min-h-0">
          <ColumnHeader
            icon={<Users className="w-3.5 h-3.5 text-primary" />}
            title="学员列表"
            extra={`${studentList.length}人`}
          />
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {studentList.length === 0 ? (
              <EmptyHint text={demoMode ? '正在生成模拟学员...' : '等待学员上线...'} />
            ) : (
              studentList.map((s) => (
                <StudentCard
                  key={s.userId}
                  student={s}
                  selected={s.userId === selectedId}
                  onSelect={() => setSelectedId(s.userId)}
                  now={now}
                  stats={statsByUser[s.userId]}
                />
              ))
            )}
          </div>
        </section>

        {/* 中栏：实时参数面板（只读） */}
        <section className="flex flex-col min-h-0 bg-bg-primary">
          <ColumnHeader
            icon={<Activity className="w-4 h-4 text-primary" />}
            title="实时参数面板"
            extra={selectedStudent ? selectedStudent.display_name : undefined}
          />
          <ParameterPanel student={selectedStudent} />
        </section>

        {/* 右栏：全班操作流 */}
        <section className="flex flex-col min-h-0 border-l border-border bg-bg-secondary">
          <ColumnHeader
            icon={<RadioTower className="w-3.5 h-3.5 text-primary" />}
            title="全班操作流"
            extra={`${actionLog.length}`}
          />
          <ActionLogList actionLog={actionLog} />
        </section>
      </main>

      {/* ===== 底部广播指令栏 ===== */}
      <footer className="flex items-center gap-2 px-4 py-2.5 bg-bg-secondary border-t border-border">
        <span className="text-xs text-text-muted mr-1">广播指令</span>
        <button
          onClick={() => handleBroadcast('pause', '暂停演练')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-warning/15 hover:bg-warning/25 text-warning border border-warning/40 rounded-lg transition-colors"
        >
          <Pause className="w-3.5 h-3.5" />
          暂停演练
        </button>
        <button
          onClick={() => handleBroadcast('hint', '发送提示')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary/15 hover:bg-primary/25 text-primary border border-primary/40 rounded-lg transition-colors"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          发送提示
        </button>
        <button
          onClick={() => handleBroadcast('force-end', '强制结束')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-danger/15 hover:bg-danger/25 text-danger border border-danger/40 rounded-lg transition-colors"
        >
          <Square className="w-3.5 h-3.5" />
          强制结束
        </button>
      </footer>
    </div>
  );
}

// =============================================================
// 子组件
// =============================================================

/** 连接状态指示器：绿=已连接 / 黄=连接中 / 灰=未连接 */
const ConnectionStatus: React.FC<{ status: ConnStatus }> = ({ status }) => {
  const cfg = {
    connected: { dot: 'bg-success', text: 'text-success', label: '已连接', pulse: false },
    connecting: { dot: 'bg-warning', text: 'text-warning', label: '连接中', pulse: true },
    disconnected: { dot: 'bg-text-muted', text: 'text-text-muted', label: '未连接', pulse: false },
  }[status];
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-bg-tertiary border border-border rounded-lg">
      <span className={`w-2 h-2 rounded-full ${cfg.dot} ${cfg.pulse ? 'animate-pulse' : ''}`} />
      <span className={`text-xs ${cfg.text}`}>{cfg.label}</span>
    </div>
  );
};

/** 栏标题 */
const ColumnHeader: React.FC<{ icon: React.ReactNode; title: string; extra?: string }> = ({
  icon,
  title,
  extra,
}) => (
  <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
    {icon}
    <span className="text-xs font-semibold text-text-secondary">{title}</span>
    {extra !== undefined && (
      <span className="ml-auto text-[11px] text-text-muted truncate max-w-[120px]">{extra}</span>
    )}
  </div>
);

/** 学员卡片 */
const StudentCard: React.FC<{
  student: StudentLiveStatus;
  selected: boolean;
  onSelect: () => void;
  now: number;
  stats?: { correct: number; total: number };
}> = ({ student, selected, onSelect, now, stats }) => {
  const diverging = (student.divergingKeys?.length ?? 0) > 0;
  const drilling = student.isDrilling;

  // 状态优先级：发散告警 > 演练中 > 空闲
  const state = diverging
    ? { dot: 'bg-danger', label: '发散告警', text: 'text-danger', ring: 'animate-pulse' }
    : drilling
      ? { dot: 'bg-success', label: '演练中', text: 'text-success', ring: '' }
      : { dot: 'bg-text-muted', label: '空闲', text: 'text-text-muted', ring: '' };

  const cardCls = selected
    ? 'bg-primary/10 border-primary shadow-sm shadow-primary/20'
    : diverging
      ? 'bg-bg-tertiary border-danger/50 hover:border-danger'
      : 'bg-bg-tertiary border-border hover:border-primary/50';

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-2 rounded-lg border transition-all ${cardCls}`}
    >
      <div className="flex items-center gap-2">
        <div className="relative flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
            {student.display_name.slice(0, 1).toUpperCase()}
          </div>
          <span
            className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-bg-secondary ${state.dot} ${state.ring}`}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium truncate">{student.display_name}</div>
          <div className={`text-[10px] ${state.text}`}>{state.label}</div>
        </div>
        {stats && stats.total > 0 && (
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] text-text-muted">正确</div>
            <div className="text-[11px] font-mono text-text-secondary">
              {stats.correct}/{stats.total}
            </div>
          </div>
        )}
      </div>

      {drilling && (student.faultName || student.difficulty) && (
        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
          {student.faultName && (
            <span className="text-[10px] text-text-secondary truncate" title={student.faultName}>
              {student.faultName}
            </span>
          )}
          {student.difficulty && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-bg-secondary text-text-muted">
              {student.difficulty}
            </span>
          )}
        </div>
      )}

      <div className="mt-1 text-[10px] text-text-muted">更新 {timeAgo(student.lastUpdate, now)}</div>
    </button>
  );
};

/** 中栏：实时参数面板（只读） */
const ParameterPanel: React.FC<{ student: StudentLiveStatus | null }> = ({ student }) => {
  if (!student || !student.isDrilling) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center text-text-muted">
          <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">请从左侧选择一名学员查看实时参数</p>
          <p className="text-xs mt-1 opacity-70">仅展示正在演练中的学员参数</p>
        </div>
      </div>
    );
  }

  const snapshot = student.equipmentSnapshot ?? {};
  const divergingSet = new Set(student.divergingKeys ?? []);
  const entries = Object.entries(snapshot);

  return (
    <div className="flex-1 overflow-y-auto p-3">
      {entries.length === 0 ? (
        <div className="text-center text-xs text-text-muted py-8">暂无参数数据</div>
      ) : (
        <div className="space-y-1">
          {entries.map(([key, value]) => {
            const diverging = divergingSet.has(key);
            return (
              <div
                key={key}
                className={`flex items-center gap-2 px-3 py-2 rounded border text-xs ${
                  diverging ? 'bg-danger/15 border-danger/40' : 'bg-bg-secondary border-border'
                }`}
              >
                <span className="flex-1 font-mono text-text-secondary truncate" title={key}>
                  {key}
                </span>
                <span
                  className={`font-mono font-semibold ${diverging ? 'text-danger' : 'text-text-primary'}`}
                >
                  {value}
                </span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                    diverging ? 'bg-danger/30 text-danger' : 'bg-success/20 text-success'
                  }`}
                >
                  {diverging ? '发散' : '正常'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** 右栏：全班操作流（最新在底部，自动滚动） */
const ActionLogList: React.FC<{ actionLog: ActionLogEntry[] }> = ({ actionLog }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [actionLog.length]);

  if (actionLog.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center text-text-muted">
          <RadioTower className="w-7 h-7 mx-auto mb-2 opacity-40" />
          <p className="text-xs">等待学员操作...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-2 space-y-1">
      {actionLog.map((e, i) => (
        <div
          key={`${e.userId}-${e.timestamp}-${i}`}
          className="flex items-start gap-2 p-2 rounded bg-bg-tertiary border border-border"
        >
          <span className="text-[10px] font-mono text-text-muted pt-0.5 flex-shrink-0">
            {fmtTime(e.timestamp)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-medium text-text-primary truncate">{e.display_name}</div>
            <div className="text-[10px] text-text-secondary break-words">{e.action}</div>
          </div>
          {e.isCorrect ? (
            <Check className="w-3.5 h-3.5 text-success flex-shrink-0 mt-0.5" />
          ) : (
            <X className="w-3.5 h-3.5 text-danger flex-shrink-0 mt-0.5" />
          )}
        </div>
      ))}
    </div>
  );
};

/** 空状态提示 */
const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <div className="text-center text-xs text-text-muted py-8">{text}</div>
);
